import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config/api";

const buildPreviousMonthRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const end = new Date(today.getFullYear(), today.getMonth(), 0);

  return {
    fromDate: start.toISOString().slice(0, 10),
    toDate: end.toISOString().slice(0, 10)
  };
};

const gstTypeOptions = [
  ["all", "All"],
  ["gst", "GST"],
  ["igst", "IGST"],
  ["sez_supply", "SEZ SUPPLY"]
];

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const formatNumber = (value, decimals = 2) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const defaultPageSize = 10;

function StatCard({ label, value, hint, tone = "amber" }) {
  const toneClasses = {
    amber: "border-amber-200 bg-amber-50",
    emerald: "border-emerald-200 bg-emerald-50",
    sky: "border-sky-200 bg-sky-50",
    violet: "border-violet-200 bg-violet-50"
  };

  return (
    <div className={`rounded-[1.4rem] border p-4 ${toneClasses[tone] || toneClasses.amber}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">{label}</p>
      <p className="mt-3 text-2xl font-bold text-stone-950">{value}</p>
      {hint ? <p className="mt-2 text-xs text-stone-600">{hint}</p> : null}
    </div>
  );
}

function TableSection({
  title,
  description,
  columns,
  rows,
  loading,
  emptyMessage,
  page,
  onPageChange,
  totalPages,
  sortKey,
  sortDirection,
  onSort
}) {
  return (
    <section className="rounded-[1.8rem] border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-lg font-bold text-stone-950">{title}</h2>
          <p className="mt-1 text-sm text-stone-500">{description}</p>
        </div>
        {totalPages > 1 ? (
          <div className="rounded-full bg-stone-100 px-4 py-2 text-sm font-semibold text-stone-700">
            Page {page} / {totalPages}
          </div>
        ) : null}
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[1080px]">
          <thead className="bg-stone-100 text-left">
            <tr>
              {columns.map((column) => (
                <th key={column.key} className="px-4 py-3 text-sm font-semibold text-stone-700">
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(column.key)}
                      className="inline-flex items-center gap-2 transition hover:text-stone-950"
                    >
                      <span>{column.label}</span>
                      <span className="text-xs">
                        {sortKey === column.key ? (sortDirection === "asc" ? "▲" : "▼") : "↕"}
                      </span>
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-stone-500">
                  Loading report data...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-stone-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, rowIndex) => (
                <tr key={`${title}-${rowIndex}`} className="border-t border-stone-200">
                  {columns.map((column) => (
                    <td key={`${column.key}-${rowIndex}`} className="px-4 py-3 text-sm text-stone-700">
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(page - 1, 1))}
            disabled={page === 1}
            className="rounded-xl border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(page + 1, totalPages))}
            disabled={page === totalPages}
            className="rounded-xl border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      ) : null}
    </section>
  );
}

export default function GstExportReports() {
  const location = useLocation();
  const isAdminView = location.pathname.startsWith("/admin");
  const defaultFilters = useMemo(() => ({
    ...buildPreviousMonthRange(),
    gstType: "all",
    customerId: "",
    invoiceNumber: ""
  }), []);

  const [filters, setFilters] = useState(defaultFilters);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState({
    gstReport: [],
    itemWiseReport: [],
    outwardSuppliesSummary: []
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("gst");
  const [gstPage, setGstPage] = useState(1);
  const [itemPage, setItemPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "Invoice Date",
    direction: "desc"
  });

  const loadCustomers = useCallback(async () => {
    try {
      const response = await axios.get(API_BASE_URL + "/api/customers");
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setCustomers([]);
    }
  }, []);

  const loadReport = useCallback(async (nextFilters) => {
    try {
      setLoading(true);
      const response = await axios.get(API_BASE_URL + "/api/gst-reports", {
        params: nextFilters
      });
      setData({
        gstReport: Array.isArray(response.data?.gstReport) ? response.data.gstReport : [],
        itemWiseReport: Array.isArray(response.data?.itemWiseReport) ? response.data.itemWiseReport : [],
        outwardSuppliesSummary: Array.isArray(response.data?.outwardSuppliesSummary) ? response.data.outwardSuppliesSummary : []
      });
      setFilters(nextFilters);
      setGstPage(1);
      setItemPage(1);
    } catch (error) {
      setData({
        gstReport: [],
        itemWiseReport: [],
        outwardSuppliesSummary: []
      });
      alert(error.response?.data?.message || "Failed to load GST report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadReport(defaultFilters);
  }, [defaultFilters, loadCustomers, loadReport]);

  const sortedGstRows = useMemo(() => {
    const rows = [...data.gstReport];
    rows.sort((left, right) => {
      if (sortConfig.key === "Invoice Date") {
        const leftValue = new Date(left["Invoice Date"] || 0).getTime();
        const rightValue = new Date(right["Invoice Date"] || 0).getTime();
        return sortConfig.direction === "asc" ? leftValue - rightValue : rightValue - leftValue;
      }

      if (sortConfig.key === "Customer Name") {
        const leftValue = String(left["Customer Name"] || "").toLowerCase();
        const rightValue = String(right["Customer Name"] || "").toLowerCase();
        return sortConfig.direction === "asc"
          ? leftValue.localeCompare(rightValue)
          : rightValue.localeCompare(leftValue);
      }

      if (sortConfig.key === "Type") {
        const leftValue = String(left.Type || "").toLowerCase();
        const rightValue = String(right.Type || "").toLowerCase();
        return sortConfig.direction === "asc"
          ? leftValue.localeCompare(rightValue)
          : rightValue.localeCompare(leftValue);
      }

      return 0;
    });
    return rows;
  }, [data.gstReport, sortConfig]);

  const gstTotalPages = Math.max(1, Math.ceil(sortedGstRows.length / defaultPageSize));
  const itemTotalPages = Math.max(1, Math.ceil(data.itemWiseReport.length / defaultPageSize));

  const paginatedGstRows = useMemo(() => {
    const start = (gstPage - 1) * defaultPageSize;
    return sortedGstRows.slice(start, start + defaultPageSize);
  }, [gstPage, sortedGstRows]);

  const paginatedItemRows = useMemo(() => {
    const start = (itemPage - 1) * defaultPageSize;
    return data.itemWiseReport.slice(start, start + defaultPageSize);
  }, [data.itemWiseReport, itemPage]);

  const summaryCards = useMemo(() => {
    const invoiceCount = data.gstReport.length;
    const invoiceValue = data.gstReport.reduce((sum, row) => sum + Number(row["Invoice Value"] || 0), 0);
    const taxableValue = data.gstReport.reduce((sum, row) => sum + Number(row["Taxable Value"] || 0), 0);
    const taxValue = data.gstReport.reduce((sum, row) =>
      sum + Number(row.IGST || 0) + Number(row["Central Tax"] || 0) + Number(row["State Tax"] || 0), 0);

    return {
      invoiceCount,
      invoiceValue,
      taxableValue,
      taxValue
    };
  }, [data.gstReport]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc"
    }));
  };

  const handleSearch = () => {
    loadReport(draftFilters);
  };

  const handleReset = () => {
    const nextDefaults = {
      ...buildPreviousMonthRange(),
      gstType: "all",
      customerId: "",
      invoiceNumber: ""
    };
    setDraftFilters(nextDefaults);
    loadReport(nextDefaults);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await axios.get(API_BASE_URL + "/api/gst-reports/export", {
        params: filters,
        responseType: "blob"
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      const date = new Date();
      link.href = blobUrl;
      link.download = `GST_Report_${String(date.getDate()).padStart(2, "0")}_${String(date.getMonth() + 1).padStart(2, "0")}_${date.getFullYear()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to export GST report");
    } finally {
      setExporting(false);
    }
  };

  const gstColumns = [
    { key: "Invoice No.", label: "Invoice No." },
    { key: "Customer Name", label: "Customer Name", sortable: true },
    { key: "GSTIN", label: "GSTIN" },
    { key: "Invoice Date", label: "Invoice Date", sortable: true, render: (value) => formatDate(value) },
    { key: "Invoice Value", label: "Invoice Value", render: (value) => formatCurrency(value) },
    { key: "Tax Rate (%)", label: "Tax Rate (%)", render: (value) => formatNumber(value) },
    { key: "Taxable Value", label: "Taxable Value", render: (value) => formatCurrency(value) },
    { key: "IGST", label: "IGST", render: (value) => formatCurrency(value) },
    { key: "Central Tax", label: "Central Tax", render: (value) => formatCurrency(value) },
    { key: "State Tax", label: "State Tax", render: (value) => formatCurrency(value) },
    { key: "Cess", label: "Cess", render: (value) => formatCurrency(value) },
    { key: "Type", label: "Type", sortable: true },
    { key: "State of Supply", label: "State of Supply" }
  ];

  const itemColumns = [
    { key: "Description", label: "Description" },
    { key: "HSN", label: "HSN" },
    { key: "Unit of Measurement", label: "Unit of Measurement" },
    { key: "Total Quantity", label: "Total Quantity", render: (value) => formatNumber(value) },
    { key: "Tax Rate (%)", label: "Tax Rate (%)", render: (value) => formatNumber(value) },
    { key: "Total Taxable Value", label: "Total Taxable Value", render: (value) => formatCurrency(value) },
    { key: "IGST", label: "IGST", render: (value) => formatCurrency(value) },
    { key: "Central Tax", label: "Central Tax", render: (value) => formatCurrency(value) },
    { key: "State Tax", label: "State Tax", render: (value) => formatCurrency(value) },
    { key: "Cess", label: "Cess", render: (value) => formatCurrency(value) }
  ];

  const summaryColumns = [
    { key: "Particulars", label: "Particulars" },
    { key: "Sl. No. From", label: "Sl. No. From" },
    { key: "Sl. No. To", label: "Sl. No. To" },
    { key: "Total Count", label: "Total Count" },
    { key: "Cancelled", label: "Cancelled" },
    { key: "Net Issued", label: "Net Issued" }
  ];

  return (
    <div className="mobile-dense-page space-y-4 lg:space-y-6">
      <section className="rounded-[2rem] border border-amber-200 bg-[linear-gradient(135deg,_#fff7ed,_#ffffff,_#f8fafc)] p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-amber-700">
              {isAdminView ? "Admin Accounts" : "Accounts"}
            </p>
            <h1 className="mt-2 text-3xl font-bold text-stone-950">GST Export &amp; Reports</h1>
            <p className="mt-2 max-w-3xl text-sm text-stone-600">
              Review GST invoice reports inside the website and export Excel reports with GST, IGST, and SEZ SUPPLY handling.
            </p>
          </div>
          <div className="rounded-[1.3rem] border border-amber-200 bg-white px-4 py-3 text-sm text-stone-600">
            <p className="font-semibold text-stone-900">Default range</p>
            <p className="mt-1">Previous month first date to previous month last date loads automatically.</p>
          </div>
        </div>
      </section>

      <section className="rounded-[1.8rem] border border-stone-200 bg-white p-5 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <input
            type="date"
            value={draftFilters.fromDate}
            onChange={(event) => setDraftFilters((current) => ({ ...current, fromDate: event.target.value }))}
            className="w-full rounded-2xl border border-amber-200 px-4 py-3 text-sm outline-none transition focus:border-amber-400"
          />
          <input
            type="date"
            value={draftFilters.toDate}
            onChange={(event) => setDraftFilters((current) => ({ ...current, toDate: event.target.value }))}
            className="w-full rounded-2xl border border-amber-200 px-4 py-3 text-sm outline-none transition focus:border-amber-400"
          />
          <select
            value={draftFilters.gstType}
            onChange={(event) => setDraftFilters((current) => ({ ...current, gstType: event.target.value }))}
            className="w-full rounded-2xl border border-amber-200 px-4 py-3 text-sm outline-none transition focus:border-amber-400"
          >
            {gstTypeOptions.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            value={draftFilters.customerId}
            onChange={(event) => setDraftFilters((current) => ({ ...current, customerId: event.target.value }))}
            className="w-full rounded-2xl border border-amber-200 px-4 py-3 text-sm outline-none transition focus:border-amber-400"
          >
            <option value="">All Customers</option>
            {customers.map((customer) => (
              <option key={customer._id} value={customer._id}>
                {customer.name || customer.customerId || "Customer"}
              </option>
            ))}
          </select>
          <input
            value={draftFilters.invoiceNumber}
            onChange={(event) => setDraftFilters((current) => ({ ...current, invoiceNumber: event.target.value }))}
            placeholder="Invoice Number"
            className="w-full rounded-2xl border border-amber-200 px-4 py-3 text-sm outline-none transition focus:border-amber-400"
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleSearch}
            className="rounded-2xl bg-stone-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-black"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="rounded-2xl border border-stone-300 px-5 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
          >
            {exporting ? "Exporting..." : "Export Excel"}
          </button>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-4">
        <StatCard label="Invoices" value={String(summaryCards.invoiceCount)} hint="Invoices in current GST report" tone="amber" />
        <StatCard label="Invoice Value" value={`Rs ${formatCurrency(summaryCards.invoiceValue)}`} hint="Total invoice value" tone="emerald" />
        <StatCard label="Taxable Value" value={`Rs ${formatCurrency(summaryCards.taxableValue)}`} hint="Taxable value across invoices" tone="sky" />
        <StatCard label="Tax Amount" value={`Rs ${formatCurrency(summaryCards.taxValue)}`} hint="IGST + Central + State tax" tone="violet" />
      </section>

      <section className="rounded-[1.8rem] border border-stone-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-3">
          {[
            { key: "gst", label: "GST Report" },
            { key: "item", label: "Item Wise Report" },
            { key: "summary", label: "Outward Supplies Summary" }
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
                activeTab === tab.key
                  ? "bg-sky-600 text-white shadow-sm"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {activeTab === "gst" ? (
        <TableSection
          title="GST Report"
          description="Invoice-wise GST report with GSTIN, tax split, invoice value, and state of supply."
          columns={gstColumns}
          rows={paginatedGstRows}
          loading={loading}
          emptyMessage="No GST invoices found for the current filters."
          page={gstPage}
          pageSize={defaultPageSize}
          onPageChange={setGstPage}
          totalPages={gstTotalPages}
          sortKey={sortConfig.key}
          sortDirection={sortConfig.direction}
          onSort={handleSort}
        />
      ) : null}

      {activeTab === "item" ? (
        <TableSection
          title="Item Wise Report"
          description="Item-wise GST aggregation grouped by description, HSN, unit, and tax rate."
          columns={itemColumns}
          rows={paginatedItemRows}
          loading={loading}
          emptyMessage="No item-wise GST records found for the current filters."
          page={itemPage}
          pageSize={defaultPageSize}
          onPageChange={setItemPage}
          totalPages={itemTotalPages}
          sortKey=""
          sortDirection="asc"
          onSort={() => {}}
        />
      ) : null}

      {activeTab === "summary" ? (
        <TableSection
          title="Outward Supplies Summary"
          description="Summary of invoice serial number range and net issued count for outward supplies."
          columns={summaryColumns}
          rows={data.outwardSuppliesSummary}
          loading={loading}
          emptyMessage="No outward supply summary available for the current filters."
          page={1}
          pageSize={defaultPageSize}
          onPageChange={() => {}}
          totalPages={1}
          sortKey=""
          sortDirection="asc"
          onSort={() => {}}
        />
      ) : null}
    </div>
  );
}
